import { useCallback, useEffect, useState } from 'react';

/**
 * Plays a one-shot animation class each time `play` is called, on an element
 * that stays mounted (a key change would remount it and drop its children's
 * state). The class is dropped when the animation ends, so the next call adds
 * it again and the browser starts the animation over.
 */
const FALLBACK_RESET_MS = 1000;

const useReplayableAnimation = (animationClassName) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const play = useCallback(() => setIsPlaying(true), []);

  // No animationend comes for an element hidden mid-animation, nor under
  // reduced motion where the class animates nothing: without this the next
  // play() would change nothing. React 18 has no onAnimationCancel prop.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const timer = setTimeout(() => setIsPlaying(false), FALLBACK_RESET_MS);
    return () => clearTimeout(timer);
  }, [isPlaying]);

  const onAnimationEnd = useCallback((event) => {
    // The children's own animations bubble up to this element too.
    if (event.target === event.currentTarget) setIsPlaying(false);
  }, []);

  return {
    animationClassName: isPlaying ? animationClassName : '',
    play,
    onAnimationEnd,
  };
};

export default useReplayableAnimation;
